// src/components/mensagens/FilterBar.jsx
// ============================================================================
// 🎯 COMPONENTE: FilterBar
// 📋 Função: Barra de busca e filtros para a lista de clientes
// 📱 Responsivo: Adaptado para mobile e desktop com animações
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Users,
  Phone,
  Clock,
  Calendar,
  AlertCircle,
  User,
  TrendingUp
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { getFilterOptions } from './helpers';

/**
 * 🎯 Componente de barra de busca e filtros
 * @param {Object} props
 * @param {string} props.search - Valor da busca
 * @param {Function} props.onSearchChange - Callback quando a busca muda
 * @param {string} props.filterType - Tipo de filtro selecionado
 * @param {Function} props.onFilterChange - Callback quando o filtro muda
 * @param {Function} props.onResetFilters - Callback para resetar filtros
 * @param {number} props.totalResults - Total de resultados encontrados
 * @param {number} props.totalClients - Total de clientes na lista
 * @param {string} props.primaryColor - Cor primária do tema
 * @param {boolean} props.showFilterBadge - Mostrar badge com número de filtros ativos
 * @param {boolean} props.autoFocus - Focar automaticamente no input
 */
export const FilterBar = ({
  search = '',
  onSearchChange,
  filterType = 'todos',
  onFilterChange,
  onResetFilters,
  totalResults = 0,
  totalClients = 0,
  primaryColor = '#10b981',
  showFilterBadge = true,
  autoFocus = false
}) => {
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [searchTerm, setSearchTerm] = useState(search);
  const searchInputRef = useRef(null);
  const filterOptions = getFilterOptions();

  // Debounce para busca (evita muitas atualizações)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== search) {
        onSearchChange?.(searchTerm);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, search, onSearchChange]);

  // Auto focus quando necessário
  useEffect(() => {
    if (autoFocus && searchInputRef.current && !isMobile) {
      searchInputRef.current.focus();
    }
  }, [autoFocus, isMobile]);

  // Contar filtros ativos
  const activeFiltersCount = [
    filterType !== 'todos' ? 1 : 0,
    search ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  // Mapear ícones para as opções de filtro
  const getFilterIcon = (iconName) => {
    const icons = {
      Users: Users,
      Phone: Phone,
      X: X,
      Clock: Clock,
      Calendar: Calendar,
      AlertCircle: AlertCircle,
      User: User,
      TrendingUp: TrendingUp
    };
    return icons[iconName] || Filter;
  };

  // Limpar busca
  const handleClearSearch = () => {
    setSearchTerm('');
    onSearchChange?.('');
  };

  // Resetar todos os filtros
  const handleResetAll = () => {
    setSearchTerm('');
    onSearchChange?.('');
    onFilterChange?.('todos');
    onResetFilters?.();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-3">
        {/* Barra principal com busca e botão de filtros */}
        <div className="flex flex-col md:flex-row gap-2">
          {/* Campo de busca */}
          <div className="flex-1 relative">
            <Search
              className={`absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 ${
                isMobile ? 'w-4 h-4' : 'w-[18px] h-[18px]'
              }`}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isMobile ? "Buscar..." : "Buscar por nome, e-mail, telefone ou mensagens..."}
              className={`w-full pl-8 pr-8 border border-gray-200 rounded-lg focus:ring-2 outline-none transition-shadow ${
                isMobile ? 'py-2.5 text-sm' : 'py-3 md:py-2.5 pl-10 pr-10'
              }`}
              style={{ focusRing: `2px solid ${primaryColor}40` }}
              aria-label="Buscar clientes"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Limpar busca"
              >
                <X size={isMobile ? 14 : 16} />
              </button>
            )}
          </div>

          {/* Botão de filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg transition-all touch:active:scale-95 ${
              showFilters
                ? 'border-green-300 text-green-700 bg-green-50'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 active:bg-gray-100'
            } ${isMobile ? 'justify-center' : ''}`}
            style={showFilters ? { backgroundColor: `${primaryColor}10`, borderColor: primaryColor } : {}}
            aria-label="Alternar filtros"
            aria-expanded={showFilters}
          >
            <Filter size={isMobile ? 16 : 18} />
            <span className={isMobile ? 'text-sm' : ''}>Filtros</span>
            {showFilterBadge && activeFiltersCount > 0 && (
              <span
                className="ml-1 px-1.5 py-0.5 text-white text-xs rounded-full"
                style={{ backgroundColor: primaryColor }}
              >
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Painel de filtros expansível */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="pt-3 mt-3 border-t border-gray-100">
                {/* Cabeçalho dos filtros */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Filter size={16} />
                    <span>Filtros</span>
                    {activeFiltersCount > 0 && (
                      <button
                        onClick={handleResetAll}
                        className="text-xs text-blue-600 hover:text-blue-700 ml-2 flex items-center gap-1"
                      >
                        <X size={12} />
                        Limpar todos
                      </button>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {totalResults} de {totalClients} clientes
                  </span>
                </div>

                {/* Botões de filtro */}
                <div className={`flex ${isMobile ? 'flex-wrap gap-1' : 'flex-wrap gap-2'}`}>
                  {filterOptions.map((option) => {
                    const Icon = getFilterIcon(option.icon);
                    const isActive = filterType === option.value;

                    return (
                      <button
                        key={option.value}
                        onClick={() => onFilterChange?.(option.value)}
                        className={`
                          inline-flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all touch:active:scale-95
                          ${isActive
                            ? 'text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                          }
                          ${isMobile ? 'text-xs' : 'text-sm'}
                        `}
                        style={isActive ? { backgroundColor: primaryColor } : {}}
                        aria-label={`Filtrar por ${option.label}`}
                        aria-pressed={isActive}
                      >
                        <Icon size={isMobile ? 12 : 14} />
                        <span>
                          {isMobile ? (option.mobileLabel || option.label.substring(0, 8)) : option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Resultados da busca (apenas mobile) */}
                {isMobile && searchTerm && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      Buscando por: <span className="font-medium text-gray-700">"{searchTerm}"</span>
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Barra de status (apenas desktop) */}
      {!isMobile && !showFilters && (
        <div className="px-3 pb-3 flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-2">
            <span>🔍 {totalResults} resultados</span>
            {activeFiltersCount > 0 && (
              <>
                <span>•</span>
                <span className="text-blue-600">
                  {activeFiltersCount} filtro(s) ativo(s)
                </span>
              </>
            )}
          </div>
          {activeFiltersCount > 0 && (
            <button
              onClick={handleResetAll}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <X size={12} />
              Limpar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 🎯 Componente de filtro rápido (versão simplificada)
 * @param {Object} props
 * @param {string} props.value - Valor do filtro
 * @param {Function} props.onChange - Callback quando muda
 * @param {Array} props.options - Opções de filtro
 * @param {string} props.primaryColor - Cor primária
 */
export const QuickFilter = ({
  value,
  onChange,
  options = [],
  primaryColor = '#10b981'
}) => {
  const isMobile = useIsMobile();

  if (options.length === 0) return null;

  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`
            px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all
            ${value === option.value
              ? 'text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
          style={value === option.value ? { backgroundColor: primaryColor } : {}}
        >
          {isMobile ? option.mobileLabel || option.label : option.label}
        </button>
      ))}
    </div>
  );
};

/**
 * 🎯 Componente de estatísticas de filtro
 * @param {Object} props
 * @param {Object} props.stats - Estatísticas dos filtros
 * @param {string} props.primaryColor - Cor primária
 */
export const FilterStats = ({ stats = {}, primaryColor = '#10b981' }) => {
  const isMobile = useIsMobile();

  const items = [
    { label: 'Pendentes', value: stats.pending, color: 'yellow' },
    { label: 'Progresso', value: stats.inProgress, color: 'blue' },
    { label: 'Concluídas', value: stats.done, color: 'green' },
    { label: 'Alta prioridade', value: stats.high, color: 'red' },
    { label: 'Atrasadas', value: stats.overdue, color: 'rose' }
  ].filter(item => item.value > 0);

  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {items.map((item) => (
        <div
          key={item.label}
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            item.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
            item.color === 'blue' ? 'bg-blue-100 text-blue-700' :
            item.color === 'green' ? 'bg-green-100 text-green-700' :
            item.color === 'red' ? 'bg-red-100 text-red-700' :
            'bg-rose-100 text-rose-700'
          }`}
        >
          <span>{item.label}:</span>
          <span className="font-bold">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// 🎯 EXPORTAÇÃO PADRÃO
// ============================================================================
export default FilterBar;